import { useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Response order: Strongly Agree → Agree → Neutral/Unsure → Disagree → Strongly Disagree (case-insensitive)
// Note: Data has inconsistent neutral labels: "Neutral / Unsure", "Neutral /Unsure", "Neutral Unsure"
const AGREE_RESPONSE_ORDER = ['strongly agree', 'somewhat agree', 'agree', 'neutral / unsure', 'neutral /unsure', 'neutral unsure', 'unsure', 'neutral', 'disagree', 'somewhat disagree', 'strongly disagree']

// Sort responses according to agree/disagree order (Unsure in the middle) - case-insensitive matching
const sortAgreeResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = AGREE_RESPONSE_ORDER.indexOf(a.label.toLowerCase())
    const bIdx = AGREE_RESPONSE_ORDER.indexOf(b.label.toLowerCase())
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

// Legend labels
const LEGEND_LABELS = ['Strongly Agree', 'Somewhat Agree', 'Neutral / Unsure', 'Somewhat Disagree', 'Strongly Disagree']

// Q11 reasons - verified against topline report
const REASONS_QUESTIONS = [
  { id: 'Agree_HealthyHabitat', label: 'Healthy habitat for birds, fish, and animals.' },
  { id: 'Agree_HealthyWaterBodies', label: 'Healthy lakes, rivers, and streams.' },
  { id: 'Agree_HealthyAirWater', label: 'Healthy air and water.' },
  { id: 'Agree_HealthyWildlife', label: 'Healthy fish and wildlife populations.' },
  { id: 'Agree_EcosystemSurvival', label: 'Survival of natural ecosystems.' },
  { id: 'Agree_SceneryBeauty', label: 'Scenery and beauty.' },
  { id: 'Agree_FamilyActivities', label: 'Healthy and affordable family activities.' },
  { id: 'Agree_PrideLandscape', label: 'Pride in our natural landscape.' },
  { id: 'Agree_OutdoorActivities', label: 'Outdoor activities like hiking, canoeing, and camping.' },
  { id: 'Agree_FoodChainSurvival', label: 'Survival of the food chain.' },
  { id: 'Agree_LocalJobs', label: 'Local jobs in conservation, parks, and tourism.' },
  { id: 'Agree_BufferZonesFire', label: 'Buffer zones to reduce wildfire risks.' },
  { id: 'Agree_PeacefulNature', label: 'Access to peaceful, quiet natural spaces with no humans.' },
  { id: 'Agree_LocalScienceJobs', label: 'Local jobs in science, parks management, and tourism.' },
  { id: 'Agree_ShieldClimate', label: 'Shielding us from climate impacts.' },
  { id: 'Agree_IndigenousConservation', label: 'Supporting Indigenous led conservation efforts.' },
  { id: 'Agree_SupportIndigenous', label: 'Supporting Indigenous values and traditional practices.' },
  { id: 'Agree_HuntFishOpp', label: 'Hunting and fishing opportunities.' },
] as const

export function ReasonsToCreate() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Sort questions by agreement level (Strongly Agree + Somewhat Agree) - highest first
  const sortedQuestions = useMemo(() => {
    if (!data) return REASONS_QUESTIONS

    const totalFilterKey = buildFilterKey('total', 'total')

    return [...REASONS_QUESTIONS].sort((a, b) => {
      const aData = data.questions[a.id]?.data[totalFilterKey]
      const bData = data.questions[b.id]?.data[totalFilterKey]

      const getAgreeScore = (qData: typeof aData) => {
        if (!qData) return 0
        const stronglyAgree = qData.responses.find(r => r.label === 'Strongly Agree' || r.label === 'Strongly agree')?.percentage || 0
        const somewhatAgree = qData.responses.find(r => r.label === 'Somewhat Agree' || r.label === 'Somewhat agree' || r.label === 'Agree')?.percentage || 0
        return stronglyAgree + somewhatAgree
      }

      return getAgreeScore(bData) - getAgreeScore(aData)
    })
  }, [data])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Reasons to Create Protected Areas</h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Please say if you AGREE or DISAGREE with the following reasons to create new provincial parks and protected areas in your province.
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('likert5', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            sortedQuestions.map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{label}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortAgreeResponses(row.data!.responses)}
                            scaleType="likert5"
                            height={isBreakActive ? 24 : 28}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
